import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Save, Lock, FileText, Palette, Bell } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { QUOTE_CURRENCIES } from '@/lib/quoteMoney';

const API_URL = process.env.REACT_APP_API_URL;

const CURRENCY_OPTIONS = QUOTE_CURRENCIES;

export function QuoteSettings() {
  const { t } = useTranslation('quotes');
  const { isPro } = useQuoteEntitlement();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState({
    company_name: '', company_email: '', company_phone: '', company_address: '',
    country_code: 'TR', currency: 'TRY', tax_label: 'KDV',
    timezone: 'Europe/Istanbul', unit_system: 'metric',
    interface_language: 'tr', default_quote_language: 'tr',
    quote_prefix: 'TEK', next_quote_number: 1,
    industry: '', logo_data_url: '', applied_templates: [],
    pdf_template: 'corporate_v2', pdf_palette: 'light_blue',
    default_tax_rate_percent: 0, tax_preset: null,
    notify_quote_events: true, followup_days: 3, expiry_reminder_days: 2,
    customer_expiry_reminders: false,
  });
  // P7 — PDF şablonu / palet seçenekleri (backend'den yüklenir)
  const [pdfOptions, setPdfOptions] = useState({ templates: [], palettes: [] });
  const [savingPdf, setSavingPdf] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, opts] = await Promise.all([
          axios.get(`${API_URL}/api/quote/profile`, { params: { include_logo: false } }),
          axios.get(`${API_URL}/api/quote/profile/pdf-options`),
        ]);
        setProfile(p.data);
        setPdfOptions({
          templates: opts.data?.templates || [],
          palettes: opts.data?.palettes || [],
        });
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.error'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (patch) => setProfile((p) => ({ ...p, ...patch }));

  /**
   * P7 — PDF şablon / palet değişikliği. Ayrı endpoint YOK; mevcut
   * güvenli `PUT /api/quote/profile` kullanılır. Backend Free/Pro
   * kilidini uygular; bu fonksiyon yalnızca izin verilen değerleri
   * gönderir (kullanıcı UI dışında bir yolla değeri değiştirse bile
   * backend son sözü söyler).
   */
  const selectPdfTemplate = async (key) => {
    const opt = pdfOptions.templates.find((x) => x.key === key);
    if (!opt) return;
    if (opt.pro_only && !isPro) {
      toast.error(t('settings.pdfTemplateProToast'), {
        action: { label: t('settings.logoUpgradeCta'), onClick: () => { window.location.href = '/quote-upgrade'; } },
      });
      return;
    }
    if (profile.pdf_template === key) return;
    setSavingPdf(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/quote/profile`, { pdf_template: key });
      setProfile(data);
      toast.success(t('settings.pdfTemplateSavedToast'));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || t('settings.pdfTemplateSaveError')));
    } finally {
      setSavingPdf(false);
    }
  };
  const selectPdfPalette = async (key) => {
    const opt = pdfOptions.palettes.find((x) => x.key === key);
    if (!opt) return;
    if (opt.pro_only && !isPro) {
      toast.error(t('settings.pdfPaletteProToast'), {
        action: { label: t('settings.logoUpgradeCta'), onClick: () => { window.location.href = '/quote-upgrade'; } },
      });
      return;
    }
    if (profile.pdf_palette === key) return;
    setSavingPdf(true);
    try {
      const { data } = await axios.put(`${API_URL}/api/quote/profile`, { pdf_palette: key });
      setProfile(data);
      toast.success(t('settings.pdfPaletteSavedToast'));
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || t('settings.pdfPaletteSaveError')));
    } finally {
      setSavingPdf(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        currency: (profile.currency || 'TRY').toUpperCase(),
        tax_label: profile.tax_label,
        quote_prefix: profile.quote_prefix,
        default_quote_language: profile.default_quote_language,
        default_tax_rate_percent: Number(profile.default_tax_rate_percent) || 0,
        notify_quote_events: Boolean(profile.notify_quote_events),
        followup_days: Number(profile.followup_days) || 0,
        expiry_reminder_days: Number(profile.expiry_reminder_days) || 0,
        customer_expiry_reminders: Boolean(profile.customer_expiry_reminders),
      };
      const { data } = await axios.put(`${API_URL}/api/quote/profile`, payload);
      setProfile(data);
      toast.success(t('quoteSettings.savedToast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <QuoteLayout>
        <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
      </QuoteLayout>
    );
  }

  return (
    <QuoteLayout>
      <div className="max-w-3xl mx-auto pb-24" data-testid="quote-settings-page">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('quoteSettings.title')}</h1>
          <p className="text-sm text-[#595959] mt-1">{t('quoteSettings.subtitle')}</p>
        </div>

        {/* ---- TEKLİF PDF AYARLARI (P7) ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6" data-testid="settings-pdf-section">
          <div className="flex items-center gap-2 mb-1">
            <FileText className="w-5 h-5 text-[#2563EB]" />
            <h2 className="text-lg font-semibold text-[#1A1A2E]">{t('settings.pdfSection')}</h2>
          </div>
          <p className="text-xs text-[#94A3B8] mb-4">
            {t('settings.pdfSectionHint')}
          </p>

          {/* Şablon kartları */}
          <div className="mb-5">
            <Label className="mb-2 flex items-center gap-1 text-sm font-medium">
              <FileText className="w-4 h-4" /> {t('settings.pdfTemplate')}
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pdfOptions.templates.map((tpl) => {
                const active = profile.pdf_template === tpl.key;
                const locked = tpl.pro_only && !isPro;
                return (
                  <button
                    type="button"
                    key={tpl.key}
                    onClick={() => selectPdfTemplate(tpl.key)}
                    disabled={savingPdf || locked}
                    aria-pressed={active}
                    className={
                      'text-left rounded-xl border p-3 transition '
                      + (active
                        ? 'border-[#2563EB] ring-2 ring-[#2563EB]/25 bg-[#EFF6FF]'
                        : locked
                          ? 'border-[#E5E5E5] bg-[#F8FAFC] opacity-70 cursor-not-allowed'
                          : 'border-[#E5E5E5] hover:border-[#2563EB]/50 hover:bg-[#F8FAFC]')
                    }
                    data-testid={`pdf-template-${tpl.key}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2">
                        {tpl.key === 'product_visual'
                          ? <div className="w-9 h-9 rounded-md bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white text-[10px] font-bold">IMG</div>
                          : <div className="w-9 h-9 rounded-md bg-gradient-to-br from-slate-700 to-slate-500 flex items-center justify-center text-white text-[10px] font-bold">V2</div>}
                        <div>
                          <div className="text-sm font-semibold text-[#1A1A2E]">{tpl.name}</div>
                          <div className="text-[11px] text-[#64748B]">
                            {tpl.key === 'product_visual'
                              ? t('settings.pdfTemplateVisualDesc')
                              : t('settings.pdfTemplateCorporateDesc')}
                          </div>
                        </div>
                      </div>
                      {locked && <Lock className="w-4 h-4 text-amber-600 shrink-0" />}
                    </div>
                    {locked && (
                      <div className="mt-2 flex items-center justify-between">
                        <span className="text-[11px] text-amber-700 font-medium">{t('settings.pdfProOnly')}</span>
                        <Link to="/quote-upgrade" className="text-[11px] font-semibold text-[#2563EB] hover:underline" data-testid="pdf-template-upgrade-link">
                          {t('settings.logoUpgradeCta')} →
                        </Link>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Renk paleti */}
          <div>
            <Label className="mb-2 flex items-center gap-1 text-sm font-medium">
              <Palette className="w-4 h-4" /> {t('settings.pdfPalette')}
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {pdfOptions.palettes.map((pal) => {
                const active = profile.pdf_palette === pal.key;
                const locked = pal.pro_only && !isPro;
                return (
                  <button
                    type="button"
                    key={pal.key}
                    onClick={() => selectPdfPalette(pal.key)}
                    disabled={savingPdf || locked}
                    aria-pressed={active}
                    className={
                      'text-left rounded-xl border p-3 transition flex items-center gap-3 '
                      + (active
                        ? 'border-[#2563EB] ring-2 ring-[#2563EB]/25 bg-[#EFF6FF]'
                        : locked
                          ? 'border-[#E5E5E5] bg-[#F8FAFC] opacity-70 cursor-not-allowed'
                          : 'border-[#E5E5E5] hover:border-[#2563EB]/50 hover:bg-[#F8FAFC]')
                    }
                    data-testid={`pdf-palette-${pal.key}`}
                  >
                    <span
                      className="w-8 h-8 rounded-full border border-white shadow-inner shrink-0"
                      style={{ backgroundColor: pal.primary }}
                      aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[#1A1A2E] flex items-center gap-1">
                        {pal.name}
                        {locked && <Lock className="w-3.5 h-3.5 text-amber-600" />}
                      </div>
                      {locked && (
                        <div className="text-[10px] text-amber-700 font-medium">{t('settings.pdfProOnly')}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
            {!isPro && (
              <div className="mt-3 text-right">
                <Link to="/quote-upgrade" className="text-xs font-semibold text-[#2563EB] hover:underline" data-testid="pdf-palette-upgrade-link">
                  {t('settings.pdfPaletteUpgradeAll')}
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* ---- DEFAULTS ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[#1A1A2E] mb-4">{t('settings.sectionDefaults')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{t('settings.currency')}</Label>
              <select
                value={profile.currency || 'TRY'}
                onChange={(e) => set({ currency: e.target.value })}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                data-testid="settings-currency"
              >
                {profile.currency && !CURRENCY_OPTIONS.some(([code]) => code === profile.currency) && (
                  <option value={profile.currency}>{profile.currency}</option>
                )}
                {CURRENCY_OPTIONS.map(([code, label]) => <option key={code} value={code}>{label}</option>)}
              </select>
            </div>
            <div>
              <Label>{t('settings.taxLabel')}</Label>
              <Input value={profile.tax_label || ''} onChange={(e) => set({ tax_label: e.target.value })} data-testid="settings-tax-label" />
              <p className="text-xs text-[#94A3B8] mt-1">{t('settings.taxLabelExamples')}</p>
            </div>
            <div>
              <Label>{t('settings.defaultTaxRate')}</Label>
              <Input
                type="number" min={0} max={100} step="0.1"
                value={profile.default_tax_rate_percent ?? 0}
                onChange={(e) => set({ default_tax_rate_percent: e.target.value })}
                data-testid="settings-default-tax-rate"
              />
              {profile.tax_preset && (
                <p className="text-xs text-[#94A3B8] mt-1">
                  {t('settings.taxPresetHint', { rate: profile.tax_preset.rate, label: profile.tax_preset.label })}
                </p>
              )}
            </div>
            <div>
              <Label>{t('settings.defaultQuoteLanguage')}</Label>
              <select
                value={profile.default_quote_language || 'tr'}
                onChange={(e) => set({ default_quote_language: e.target.value })}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white"
                data-testid="settings-default-quote-language"
              >
                <option value="tr">Türkçe</option>
                <option value="en">English</option>
                <option value="de">Deutsch</option>
                <option value="fr">Français</option>
                <option value="es">Español</option>
              </select>
            </div>
            <div>
              <Label>{t('settings.quotePrefix')}</Label>
              <Input maxLength={10} value={profile.quote_prefix ?? 'TEK'} onChange={(e) => set({ quote_prefix: e.target.value })} data-testid="settings-quote-prefix" />
            </div>
            <div>
              <Label>{t('settings.nextQuoteNumber')}</Label>
              <Input value={profile.next_quote_number || 1} disabled />
              <p className="text-xs text-[#94A3B8] mt-1">{t('settings.nextQuoteNumberHint')}</p>
            </div>
          </div>
        </div>

        {/* ---- NOTIFICATIONS & FOLLOW-UPS ---- */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 mb-6" data-testid="settings-notifications-section">
          <div className="flex items-center gap-2 mb-1">
            <Bell className="w-5 h-5 text-[#2563EB]" />
            <h2 className="text-lg font-semibold text-[#1A1A2E]">{t('settings.notifySection')}</h2>
          </div>
          <p className="text-xs text-[#94A3B8] mb-4">{t('settings.notifySectionHint')}</p>
          <label className="flex items-start gap-3 py-2 cursor-pointer">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={!!profile.notify_quote_events}
              onChange={(e) => set({ notify_quote_events: e.target.checked })} data-testid="settings-notify-events" />
            <span>
              <span className="block text-sm font-medium text-[#1A1A2E]">{t('settings.notifyEvents')}</span>
              <span className="block text-xs text-[#64748B]">{t('settings.notifyEventsHint')}</span>
            </span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
            <div>
              <Label>{t('settings.followupDays')}</Label>
              <select value={String(profile.followup_days ?? 3)} onChange={(e) => set({ followup_days: Number(e.target.value) })}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white" data-testid="settings-followup-days">
                <option value="0">{t('settings.off')}</option>
                {[1, 2, 3, 5, 7, 10, 14].map((d) => <option key={d} value={d}>{t('settings.daysCount', { count: d })}</option>)}
              </select>
              <p className="text-xs text-[#94A3B8] mt-1">{t('settings.followupDaysHint')}</p>
            </div>
            <div>
              <Label>{t('settings.expiryReminderDays')}</Label>
              <select value={String(profile.expiry_reminder_days ?? 2)} onChange={(e) => set({ expiry_reminder_days: Number(e.target.value) })}
                className="h-10 w-full border border-[#E5E5E5] rounded-md px-3 text-sm bg-white" data-testid="settings-expiry-days">
                <option value="0">{t('settings.off')}</option>
                {[1, 2, 3, 5, 7].map((d) => <option key={d} value={d}>{t('settings.daysCount', { count: d })}</option>)}
              </select>
              <p className="text-xs text-[#94A3B8] mt-1">{t('settings.expiryReminderDaysHint')}</p>
            </div>
          </div>
          <label className="flex items-start gap-3 py-2 mt-3 cursor-pointer">
            <input type="checkbox" className="mt-1 h-4 w-4" checked={!!profile.customer_expiry_reminders}
              onChange={(e) => set({ customer_expiry_reminders: e.target.checked })} data-testid="settings-customer-expiry" />
            <span>
              <span className="block text-sm font-medium text-[#1A1A2E]">{t('settings.customerExpiry')}</span>
              <span className="block text-xs text-[#64748B]">{t('settings.customerExpiryHint')}</span>
            </span>
          </label>
        </div>

        <div className="flex justify-end mb-8">
          <Button
            onClick={save} disabled={saving}
            className="bg-[#2563EB] hover:bg-[#1E4ED8]"
            data-testid="settings-save-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? t('quoteSettings.savingButton') : t('quoteSettings.saveButton')}
          </Button>
        </div>

      </div>
    </QuoteLayout>
  );
}

export default QuoteSettings;
